/*
eslint-disable no-restricted-imports */
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Text,
} from "@chakra-ui/react";
import { FieldArray, useFormikContext } from "formik";
import { InputControl } from "formik-chakra-ui";
import _ from "lodash";
import React from "react";
import { RiArchiveLine, RiDeleteBin6Line } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { Subgrid, SubgridLayout } from "src/components/Layout/Subgrid";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import * as Question from "src/services/formTemplate/question";
import { isNonEmptyString } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { generateKey } from "../../../../autoGenerateKey";
import { useEditFormTemplateContext } from "../../../../context";
import { useLiveEditConfirmation } from "../../../LiveEditConfirmation";
import { RelatedQuestionVisibility } from "../../../QuestionVisibility/QuestionVisibility";
import { ArchivedTag } from "../../QuestionExtraInfo/ArchivedTag";
import { SchoolItem } from "../../types";
import * as Eligibility from "./Eligibility";
import { AnswerOptionFormikType, FormikType, MAX_OPTIONS } from "./formik";
import { MoveAnswerOptionPopover } from "./MoveAnswerOptionPopover";

type Props = {
  sectionType: AF.V2.SectionType;
  question: AF.V2.Question | undefined;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  otherQuestions: readonly AF.V2.Question[];
};
export const AnswersOptions: React.FC<Props> = ({
  sectionType,
  schools,
  question,
  otherQuestions,
}) => {
  const formik = useFormikContext<FormikType>();
  const questionType = formik.values.type;
  const options = formik.values.options;
  const context = useEditFormTemplateContext();

  const Confirmation = useConfirmationDialog({
    body: "",
    header: "Edit answer values?",
  });

  const LiveEditConfirmation = useLiveEditConfirmation();

  const ArchiveConfirmation = useConfirmationDialog({
    body: (
      <Flex direction="column" gap="4">
        <Text>
          This will archive the answer, <b>hiding it completely</b> from
          responses on both Staff and Family sides. Data will not be deleted and
          will continue to be visible in exports.
        </Text>
        <Text>
          Any <b>conditionality</b> associated with this answer will be removed,
          and may require manual fixing. This can't be undone or recovered.
        </Text>
      </Flex>
    ),
    header: "Archive answer?",
    confirmButton: {
      label: "Yes, archive answer",
    },
  });

  const UnarchiveConfirmation = useConfirmationDialog({
    body: (
      <Flex direction="column" gap="4">
        <Text>
          This will unarchive the answer, making it visible and selectable again
          on both Staff and Family sides.
        </Text>
      </Flex>
    ),
    header: "Unarchive answer?",
    confirmButton: {
      label: "Yes, unarchive answer",
    },
  });

  React.useEffect(() => {
    (async () => {
      if (
        formik.values.type !== "SingleSelect" &&
        formik.values.type !== "MultiSelect"
      ) {
        await formik.setFieldValue("options", []);
      } else if (formik.values.options.length === 0) {
        await formik.setFieldValue("options", [newOption()]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.type]);

  const optionsError: string | undefined =
    _.isString(formik.errors.options) && isNonEmptyString(formik.errors.options)
      ? formik.errors.options
      : undefined;

  if (!Question.hasOptions({ type: questionType })) {
    return null;
  }

  return (
    <Flex direction="column" gap="3" alignItems="flex-start">
      <FieldArray
        name="options"
        render={(arrayHelpers) => (
          <>
            {options.map((option, index) => {
              const labelName = `options.${index}.label`;
              const valueName = `options.${index}.value`;
              const userGeneratedValueName = `options.${index}.userGeneratedValue`;
              const isUnpublished = context.helpers.isUnpublishedAnswerOption(
                question?.id || "",
                option.id
              );
              const isDisabled = option.isArchived;
              return (
                <Card key={option.id} showBorder useFlex width="100%">
                  {option.isArchived && <ArchivedTag />}
                  <SubgridLayout
                    columns={[
                      ["col-1", "1fr"],
                      ["col-2", "1fr"],
                      ["col-3", "auto"],
                      ["col-4", "auto"],
                    ]}
                    rows={[
                      ["label", "auto"],
                      ["input", "auto"],
                      ["error", "auto"],
                    ]}
                    gap="0"
                    columnGap="4"
                  >
                    <InputControl
                      as={Subgrid}
                      id={`${option.id}.label`}
                      name={labelName}
                      label={`Answer ${index + 1}`}
                      isDisabled={isDisabled}
                      inputProps={{
                        onChange: async (event) => {
                          const label = event.target.value;
                          const shouldChangeValue =
                            isUnpublished &&
                            formik.values.options[`${index}`]
                              ?.userGeneratedValue === false;

                          if (
                            !isUnpublished &&
                            !(await LiveEditConfirmation.confirmAnswersOptions())
                          ) {
                            return;
                          }
                          await formik.setFieldValue(labelName, label, false);

                          if (shouldChangeValue) {
                            await formik.setFieldValue(
                              valueName,
                              generateKey(label),
                              false
                            );
                          }
                        },
                      }}
                    />

                    <InputControl
                      as={Subgrid}
                      id={`${option.id}.value`}
                      name={valueName}
                      label="Answer value"
                      isDisabled={isDisabled}
                      inputProps={{
                        placeholder: "E.g.: RED",
                        value: option.value,
                        onChange: async (event) => {
                          const inputElement = event.target as HTMLInputElement;
                          const value = inputElement.value;
                          const userGeneratedValue = value.trim().length > 0;
                          await formik.setFieldValue(valueName, value, false);
                          await formik.setFieldValue(
                            userGeneratedValueName,
                            userGeneratedValue,
                            false
                          );
                          return;
                        },
                      }}
                    />
                    {isUnpublished ? (
                      <Button
                        leftIcon={<RiDeleteBin6Line />}
                        gridColumnStart="col-3"
                        gridRowStart="input"
                        flexShrink={0}
                        variant="outline"
                        colorScheme="gray"
                        onClick={async () => {
                          await arrayHelpers.remove(index);
                        }}
                      >
                        Remove answer
                      </Button>
                    ) : option.isArchived ? (
                      <Button
                        leftIcon={<RiArchiveLine />}
                        gridColumnStart="col-3"
                        gridRowStart="input"
                        flexShrink={0}
                        variant="outline"
                        colorScheme="gray"
                        onClick={async () => {
                          if (await UnarchiveConfirmation.confirm()) {
                            const updatedOptions = [...options];
                            const currentOption = updatedOptions[index];
                            if (currentOption) {
                              updatedOptions[index] = {
                                ...currentOption,
                                isArchived: false,
                              };
                            }
                            await formik.setFieldValue(
                              "options",
                              updatedOptions
                            );
                          }
                        }}
                      >
                        Unarchive answer
                      </Button>
                    ) : (
                      <Button
                        leftIcon={<RiArchiveLine />}
                        gridColumnStart="col-3"
                        gridRowStart="input"
                        flexShrink={0}
                        variant="outline"
                        colorScheme="gray"
                        isDisabled={isDisabled}
                        onClick={async () => {
                          if (await ArchiveConfirmation.confirm()) {
                            const updatedOptions = [...options];
                            const currentOption = updatedOptions[index];
                            if (currentOption) {
                              updatedOptions[index] = {
                                ...currentOption,
                                isArchived: true,
                                eligibility: undefined,
                              };
                            }
                            await formik.setFieldValue(
                              "options",
                              updatedOptions
                            );
                          }
                        }}
                      >
                        Archive answer
                      </Button>
                    )}
                    <MoveAnswerOptionPopover
                      gridColumnStart="col-4"
                      gridRowStart="input"
                      optionId={option.id}
                      optionIndex={index}
                      arrayHelpers={arrayHelpers}
                      isDisabled={isDisabled}
                    />
                  </SubgridLayout>

                  {!isDisabled && (
                    <Flex direction="column" paddingLeft="2rem" gap="3">
                      {question?.key !== undefined && option.value ? (
                        <RelatedQuestionVisibility
                          questionId={question.id}
                          answerId={option.id}
                          otherQuestions={otherQuestions}
                        >
                          {(relatedQuestions) => (
                            <Box>When chosen, reveals {relatedQuestions}</Box>
                          )}
                        </RelatedQuestionVisibility>
                      ) : null}
                      <Eligibility.EligibilityCheckboxCard
                        sectionType={sectionType}
                        questionType={questionType}
                        optionIndex={index}
                        schools={schools}
                      />
                    </Flex>
                  )}
                </Card>
              );
            })}

            <FormControl isInvalid={isNonEmptyString(optionsError)}>
              <Flex direction="row" gap="3" alignItems="center">
                <Button
                  colorScheme="gray"
                  onClick={async () => {
                    arrayHelpers.push(newOption());
                  }}
                  isDisabled={options.length >= MAX_OPTIONS}
                >
                  Add answer
                </Button>
                <FormHelperText margin={0}>
                  Maximum limit of 500 answers
                </FormHelperText>
              </Flex>
              <FormErrorMessage>{optionsError}</FormErrorMessage>
            </FormControl>
          </>
        )}
      />

      {Confirmation.confirmationDialog}
      {ArchiveConfirmation.confirmationDialog}
      {UnarchiveConfirmation.confirmationDialog}
    </Flex>
  );
};

function newOption(): AnswerOptionFormikType {
  return {
    id: window.crypto.randomUUID(),
    label: "",
    value: "",
    userGeneratedValue: false,
    isArchived: false,
  };
}
